import { BaseProcess } from '../../systems/base-process.js';
import { ProcessPriority } from '../../systems/process.js';
import type { ProcessContext } from '../../systems/process.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { CalculatorView } from './calculator-view.js';
import type { CalculatorState, CalculatorSequenceState } from './calculator-view.js';
import type { VisualizationSignal } from '../../types/visualization.js';

export class CalculatorProcess extends BaseProcess {
  kind = 'calculator';

  metadata = {
    priority: ProcessPriority.NORMAL,
    category: 'utility' as const,
    canStop: true,
    preserveState: true,
    timeSensitive: false
  };

  private state: CalculatorState = {
    displayValue: '0',
    history: [],
    mode: 'interactive'
  };

  // Internal state for calculation logic
  private currentExpression: string = '';
  private isNewCalculation: boolean = true;
  
  // Playback timer
  private playbackInterval: number | null = null;

  constructor(id: string) {
    super(id);
  }

  protected onStart(ctx: ProcessContext): void {
    const view = new CalculatorView(this.id, `${this.id}-main`, this.state);
    ctx.registerView(view.toViewInstance());

    // Listen for UI actions (calculator buttons)
    this.onEvent('calculator.action', (payload: unknown) => {
      const data = payload as { processId: string; action: string };
      if (data.processId === this.id) {
        // If sequence is active and playing, pause it on interaction
        if (this.state.sequence?.isPlaying) {
            this.pausePlayback();
        }
        this.handleAction(data.action);
      }
    });

    // Listen for Playback controls
    this.onEvent('calculator.playback', (payload: unknown) => {
        const data = payload as { processId: string; action: string };
        if (data.processId === this.id) {
            this.handlePlaybackControl(data.action);
        }
    });

    // Listen for Cortex/Remote actions
    this.onEvent('calculator.remote-command', (payload: unknown) => {
      const data = payload as { processId: string; command: string; value?: string };
      if (data.processId === this.id) {
        this.handleRemoteCommand(data.command, data.value);
      }
    });

    // Listen for Visualization Signals (Cortex API contract)
    this.onEvent('visualization.signal', (payload: unknown) => {
        // Check if this signal targets the calculator or is a sequence type
        const signal = payload as VisualizationSignal;
        // Note: VisualizationProcess will ignore this if it's type 'CALCULATOR_SEQUENCE'
        // because we haven't registered a real renderer for it in the registry (only a placeholder).
        // This process owns the logic.
        if (signal.type === 'CALCULATOR_SEQUENCE') {
            this.handleVisualizationSignal(signal);
        }
    });
    
    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  private handleVisualizationSignal(signal: VisualizationSignal): void {
      // Using the data payload to populate sequence
      if (signal.data && Array.isArray(signal.data.steps)) {
          this.state.sequence = {
              steps: signal.data.steps,
              currentIndex: 0,
              isPlaying: signal.data.autoPlay ?? false,
              title: signal.title
          };
          
          // Reset calculator state for clean playback start
          this.handleAction('C');
          
          this.updateView();

          if (this.state.sequence.isPlaying) {
              this.startPlayback();
          }
      }
  }

  private handlePlaybackControl(action: string): void {
      if (!this.state.sequence) return;

      switch (action) {
          case 'toggle-play':
              if (this.state.sequence.isPlaying) {
                  this.pausePlayback();
              } else {
                  this.startPlayback();
              }
              break;
          case 'next':
              this.pausePlayback();
              this.stepForward();
              break;
          case 'prev':
              this.pausePlayback();
              this.stepBackward();
              break;
      }
  }

  private startPlayback(): void {
      if (!this.state.sequence) return;
      
      this.state.sequence.isPlaying = true;
      this.updateView();

      if (this.playbackInterval) clearInterval(this.playbackInterval);
      
      this.playbackInterval = window.setInterval(() => {
          this.stepForward();
      }, 1000); // 1 second per step
  }

  private pausePlayback(): void {
      if (!this.state.sequence) return;
      
      this.state.sequence.isPlaying = false;
      if (this.playbackInterval) {
          clearInterval(this.playbackInterval);
          this.playbackInterval = null;
      }
      this.updateView();
  }

  private stepForward(): void {
      if (!this.state.sequence) return;

      // If we are at the end, stop
      if (this.state.sequence.currentIndex >= this.state.sequence.steps.length) {
          this.pausePlayback();
          return;
      }

      // Execute the current step's action
      const step = this.state.sequence.steps[this.state.sequence.currentIndex];
      this.handleAction(step.action);

      // Advance index
      this.state.sequence.currentIndex++;
      
      // If we just finished the last step, pause but keep index at end to show completion
      if (this.state.sequence.currentIndex >= this.state.sequence.steps.length) {
           this.pausePlayback();
      }

      this.updateView();
  }

  private stepBackward(): void {
       if (!this.state.sequence) return;

       // If at start, do nothing
       if (this.state.sequence.currentIndex <= 0) return;

       // To go back, we essentially have to reset and replay up to n-1
       // This is because calculator state is cumulative. 
       // A simple undo would require storing full state history.
       // For simplicity: Reset and fast-forward.
       
       const targetIndex = this.state.sequence.currentIndex - 1;
       
       // Reset calculator
       this.currentExpression = '';
       this.state.displayValue = '0';
       this.isNewCalculation = true;
       this.state.history = []; // Clear history or restore from snapshot if we had one
       
       // Replay up to targetIndex
       for (let i = 0; i < targetIndex; i++) {
           this.handleAction(this.state.sequence.steps[i].action);
       }
       
       this.state.sequence.currentIndex = targetIndex;
       this.updateView();
  }

  private handleRemoteCommand(command: string, value?: string): void {
    switch (command) {
      case 'EVALUATE':
        if (value) {
          this.currentExpression = value;
          this.calculate();
        }
        break;
      case 'CLEAR':
        this.handleAction('C');
        break;
      case 'PRESS':
        if (value) {
          this.handleAction(value);
        }
        break;
      case 'SET_MODE':
        if (value === 'interactive' || value === 'remote-only') {
          this.state.mode = value;
          this.updateView();
        }
        break;
    }
  }

  private handleAction(action: string): void {
    if (action === 'C') {
      this.currentExpression = '';
      this.state.displayValue = '0';
      this.isNewCalculation = true;
    } else if (action === '=') {
      this.calculate();
    } else {
      if (this.isNewCalculation) {
        if (['+', '*', '/', ')'].includes(action)) {
           // If starting with an operator, assume starting from 0 (except for minus which can be unary)
           this.currentExpression = '0' + action;
        } else {
           this.currentExpression = action;
        }
        this.isNewCalculation = false;
      } else {
        this.currentExpression += action;
      }
      this.state.displayValue = this.currentExpression;
    }

    this.updateView();
  }

  private calculate(): void {
    try {
      // Basic sanitization: only allow digits, operators, parens, dot
      const sanitized = this.currentExpression.replace(/[^0-9+\-*/().]/g, '');
      
      // eslint-disable-next-line no-new-func
      const result = new Function(`return (${sanitized})`)();
      
      // Format result
      let resultStr = String(result);
      
      // Handle limiting decimals
      if (resultStr.includes('.')) {
        const num = parseFloat(resultStr);
        resultStr = parseFloat(num.toFixed(8)).toString(); // Limit to 8 decimal places and remove trailing zeros
      }
      
      this.state.history.push(`${this.currentExpression} = ${resultStr}`);
      if (this.state.history.length > 5) {
        this.state.history.shift(); // Keep last 5
      }
      
      this.state.displayValue = resultStr;
      this.currentExpression = resultStr;
      this.isNewCalculation = true; // Next input will start new expression unless it's an operator
    } catch (e) {
      this.state.displayValue = 'Error';
      this.currentExpression = '';
      this.isNewCalculation = true;
    }
    
    this.updateView();
  }

  private updateView(): void {
    const view = this.views.get(`${this.id}-main`);
    if (view && view.data && (view.data as any)._baseViewInstance) {
      const instance = (view.data as any)._baseViewInstance as CalculatorView;
      instance.updateState(this.state);
    }
  }
  
  protected onStop(): void {
      if (this.playbackInterval) {
          clearInterval(this.playbackInterval);
      }
      super.onStop();
  }
}

// Register the process
registerProcess('calculator', (id: string) => new CalculatorProcess(id));
